import { create } from 'zustand';
import {
  listDepts,
  createDept as apiCreateDept,
  updateDept as apiUpdateDept,
  softDeleteDept as apiSoftDeleteDept,
  reactivateDept as apiReactivateDept,
  assignMember as apiAssignMember,
  unassignMember as apiUnassignMember,
  listDeptMembers as apiListDeptMembers,
  listMemberDepts as apiListMemberDepts,
} from '../services/api/departmentApi';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * Department Store (Zustand) — EPIC A / A1
 *
 * Caches:
 *   - `departments` — the full org dept list (active by default; the whole
 *     list refetches when `includeInactive` flips, which IS a backend filter).
 *   - `membersByDept` — keyed map dept_id → [{member_id,name,role}]; lazily
 *     loaded by the assignment UI and the dept-settings member-count drill-in.
 *   - `deptsByMember` — keyed map member_id → [{dept_id,name}]; the member→
 *     departments direction (the DepartmentAssignment modal's source of truth).
 *
 * Single source of truth: dept `member_count` shown in the settings list and
 * the member's dept-chip count both derive from these caches — never a
 * hardcoded zero default.
 *
 * Invalidation: ANY write (create / update / soft-delete / assign / unassign)
 * refetches the affected slice rather than patching blind, so counts can't
 * drift. Assign/unassign apply an OPTIMISTIC update to `deptsByMember` first
 * for snappy UI, then reconcile against the server (rollback on failure).
 *
 * Re-fetch-on-mutation mirrors procedureStore / doctorTeamStore — cheap at
 * clinic-scale row counts.
 * ═══════════════════════════════════════════════════════════════════════
 */

const useDepartmentStore = create((set, get) => ({
  // ── State ──────────────────────────────────────────────────────────
  departments: [],
  membersByDept: {},   // dept_id → [{member_id, name, role}]
  deptsByMember: {},   // member_id → [{dept_id, name}]
  includeInactive: false,
  deptsLoaded: false,
  isLoading: false,
  error: null,
  // hydrateAllMemberDepts guard — dedup concurrent calls + skip once done, so a
  // consumer effect re-firing (unstable dep) can't storm one-request-per-dept.
  _hydrateAllInFlight: null,
  _allMemberDeptsHydrated: false,

  // ── Fetch dept list ────────────────────────────────────────────────
  fetchDepartments: async () => {
    const { includeInactive } = get();
    set({ isLoading: true, error: null });

    const result = await listDepts({ include_inactive: includeInactive });

    if (result.success) {
      set({ departments: result.data.departments || [], isLoading: false, deptsLoaded: true });
    } else {
      // Mark loaded even on failure so consumers don't hang.
      set({ error: result.error, isLoading: false, deptsLoaded: true });
    }
    return result;
  },

  setIncludeInactive: (value) => {
    set({ includeInactive: !!value });
    return get().fetchDepartments();
  },

  // ── Create ─────────────────────────────────────────────────────────
  createDepartment: async (body) => {
    const result = await apiCreateDept(body);
    if (result.success) {
      // Refetch so order / member_count / filters stay consistent.
      await get().fetchDepartments();
    }
    return result;
  },

  // ── Update (partial) ───────────────────────────────────────────────
  updateDepartment: async (id, body) => {
    const result = await apiUpdateDept(id, body);
    if (result.success) {
      // Patch in place to preserve scroll; member_count unaffected by rename.
      set((s) => ({
        departments: s.departments.map((d) =>
          d.id === id ? { ...d, ...(result.data || body) } : d
        ),
      }));
      // Member→dept caches carry the dept name — refresh them lazily on next
      // open; cheap to just drop stale name references here.
      set((s) => {
        const next = {};
        for (const [mid, depts] of Object.entries(s.deptsByMember)) {
          next[mid] = depts.map((d) =>
            d.dept_id === id ? { ...d, ...(body.name ? { name: body.name } : {}) } : d
          );
        }
        return { deptsByMember: next };
      });
    }
    return result;
  },

  // ── Soft-delete (deactivate) ───────────────────────────────────────
  // Returns the api result; caller reads result.data?.member_count to show the
  // post-deactivation warning. The pre-confirm warning count comes from the
  // dept row's member_count (single source of truth) before this is called.
  softDeleteDepartment: async (id) => {
    const result = await apiSoftDeleteDept(id);
    if (result.success) {
      const { includeInactive } = get();
      set((s) => ({
        departments: includeInactive
          ? s.departments.map((d) => (d.id === id ? { ...d, active: false } : d))
          : s.departments.filter((d) => d.id !== id),
      }));
    }
    return result;
  },

  // ── Reactivate (un-delete) ─────────────────────────────────────────
  // Optimistically flips the row active (only the include-inactive list shows
  // deactivated rows, so the row is always present here), then reconciles the
  // whole list from the server so member_count / order stay consistent. Rolls
  // back the optimistic flip on failure.
  reactivateDepartment: async (id) => {
    const prev = get().departments;
    set((s) => ({
      departments: s.departments.map((d) => (d.id === id ? { ...d, active: true } : d)),
    }));

    const result = await apiReactivateDept(id);

    if (result.success) {
      // Invalidate the list cache (member_count comes back fresh on the row).
      await get().fetchDepartments();
    } else {
      set({ departments: prev });
    }
    return result;
  },

  // ── Membership reads ───────────────────────────────────────────────
  fetchDeptMembers: async (deptId) => {
    const result = await apiListDeptMembers(deptId);
    if (result.success) {
      set((s) => ({
        membersByDept: { ...s.membersByDept, [deptId]: result.data.members || [] },
      }));
    }
    return result;
  },

  fetchMemberDepts: async (memberId) => {
    const result = await apiListMemberDepts(memberId);
    if (result.success) {
      // Backend membership edges carry dept_id but NOT the dept name. Join the
      // name from the dept-list cache (single source of truth for dept names)
      // so chips render the real name, falling back to dept_id only if the
      // dept list hasn't loaded yet.
      const byId = {};
      for (const d of get().departments) byId[d.dept_id || d.id] = d.name;
      const edges = (result.data.departments || []).map((e) => ({
        ...e,
        name: e.name || byId[e.dept_id] || e.dept_id,
      }));
      set((s) => ({
        deptsByMember: { ...s.deptsByMember, [memberId]: edges },
      }));
    }
    return result;
  },

  // ── Bulk hydrate member→dept map for a list view (cost-aware) ───────
  // Populates the canonical `deptsByMember` for ALL members using ONE fetch
  // per DEPARTMENT (fetchDeptMembers) and inverting, instead of one fetch per
  // MEMBER (fetchMemberDepts). D (# departments) << N (# staff), so this is
  // the cheap path for the Manage-Staff Department column. Reuses the same
  // caches the assignment modal reconciles into — single source of truth.
  // IDEMPOTENT: an unstable consumer effect (e.g. useScope's viewerDeptIds churning
  // a new array ref every render) used to storm this one-request-per-department.
  // Guard order: (1) return the in-flight promise so concurrent callers share one
  // run; (2) skip entirely once hydrated (no refetch); (3) otherwise run the body
  // once, flag success, and always clear the in-flight handle. A real membership
  // change (assign/unassign) resets the done-flag so the next call re-hydrates.
  hydrateAllMemberDepts: async () => {
    const { _hydrateAllInFlight, _allMemberDeptsHydrated } = get();
    if (_hydrateAllInFlight) return _hydrateAllInFlight;
    if (_allMemberDeptsHydrated) return { success: true };

    const run = (async () => {
      if (!get().deptsLoaded) await get().fetchDepartments();
      const active = (get().departments || []).filter((d) => d.active !== false);
      // One fetch per department (parallel); no-op if the org has no departments.
      await Promise.all(active.map((d) => get().fetchDeptMembers(d.dept_id || d.id)));
      const membersByDept = get().membersByDept;
      const byMember = {};
      for (const d of active) {
        const did = d.dept_id || d.id;
        for (const m of (membersByDept[did] || [])) {
          const mid = m.member_id;
          if (!mid) continue;
          if (!byMember[mid]) byMember[mid] = [];
          if (!byMember[mid].some((x) => x.dept_id === did)) {
            byMember[mid].push({ dept_id: did, name: d.name });
          }
        }
      }
      set((s) => ({ deptsByMember: { ...s.deptsByMember, ...byMember }, _allMemberDeptsHydrated: true }));
      return { success: true };
    })().finally(() => set({ _hydrateAllInFlight: null }));

    set({ _hydrateAllInFlight: run });
    return run;
  },

  // ── Membership writes (optimistic + reconcile) ─────────────────────
  /**
   * Assign a member to a department. Optimistically adds the dept to the
   * member's dept list, then reconciles from the server. Rolls back on failure.
   *
   * @param {string} memberId
   * @param {{ dept_id: string, name?: string }} dept  the target dept (carries name for the chip)
   */
  assignMemberToDept: async (memberId, dept) => {
    const prev = get().deptsByMember[memberId] || [];
    // Optimistic: add unless already present.
    if (!prev.some((d) => d.dept_id === dept.dept_id)) {
      set((s) => ({
        deptsByMember: {
          ...s.deptsByMember,
          [memberId]: [...(s.deptsByMember[memberId] || []), { dept_id: dept.dept_id, name: dept.name }],
        },
      }));
    }

    const result = await apiAssignMember(dept.dept_id, memberId);

    if (result.success) {
      // Membership changed → let the bulk map re-hydrate once on next demand.
      set({ _allMemberDeptsHydrated: false });
      // Reconcile both directions from the server (also bumps member_count).
      await get().fetchMemberDepts(memberId);
      await get().fetchDepartments();
    } else {
      // Rollback optimistic add.
      set((s) => ({ deptsByMember: { ...s.deptsByMember, [memberId]: prev } }));
    }
    return result;
  },

  /**
   * Unassign a member from a department. Optimistically removes the dept from
   * the member's dept list, then reconciles. Rolls back on failure.
   */
  unassignMemberFromDept: async (memberId, deptId) => {
    const prev = get().deptsByMember[memberId] || [];
    set((s) => ({
      deptsByMember: {
        ...s.deptsByMember,
        [memberId]: prev.filter((d) => d.dept_id !== deptId),
      },
    }));

    const result = await apiUnassignMember(deptId, memberId);

    if (result.success) {
      // Membership changed → let the bulk map re-hydrate once on next demand.
      set({ _allMemberDeptsHydrated: false });
      await get().fetchMemberDepts(memberId);
      await get().fetchDepartments();
    } else {
      set((s) => ({ deptsByMember: { ...s.deptsByMember, [memberId]: prev } }));
    }
    return result;
  },

  clearError: () => set({ error: null }),

  // Test/utility hook — reset to a clean slate.
  reset: () => set({
    departments: [],
    membersByDept: {},
    deptsByMember: {},
    includeInactive: false,
    deptsLoaded: false,
    isLoading: false,
    error: null,
    _hydrateAllInFlight: null,
    _allMemberDeptsHydrated: false,
  }),
}));

export default useDepartmentStore;
